import {
  createRootRoute,
  Outlet,
  redirect,
  useRouter,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { resolveRedirect } from '../config/redirects';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { useEffect } from 'react';
import type { User } from 'oidc-client-ts';
import Loader from '../components/Loader/Loader';
import TopLoadingBar from '../components/TopLoadingBar/TopLoadingBar';
import { BrandProvider } from '../context/BrandContext';

interface RouterContext {
  auth: {
    user: User | null;
    isLoading: boolean;
    login: () => Promise<void>;
    logout: () => Promise<void>;
  };
}

const UNPROTECTED_PATHS = ['/callback', '/impersonation/start'];

const RootLayout = () => {
  const router = useRouter();
  const { auth } = router.options.context as RouterContext;
  // useRouterState creates a reactive subscription — re-renders on every navigation.
  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  const isImpersonating = !!sessionStorage.getItem('impersonation_id_token');

  useEffect(() => {
    if (UNPROTECTED_PATHS.includes(currentPath) || isImpersonating) return;

    if (!auth.isLoading && !auth.user) {
      auth.login();
    }
  }, [auth, currentPath, isImpersonating]);

  if (!UNPROTECTED_PATHS.includes(currentPath) && !auth.user && !isImpersonating) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '1.2rem',
        }}
      >
        <Loader fullScreen />
      </div>
    );
  }

  return (
    <BrandProvider>
      <TopLoadingBar />
      <Outlet />
      <TanStackRouterDevtools position="bottom-right" />
    </BrandProvider>
  );
};

function NotFound() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate({ to: '/', replace: true });
  }, []);
  return null;
}

export const Route = createRootRoute({
  beforeLoad: ({ location }) => {
    const dest = resolveRedirect(location.pathname);
    if (dest) throw redirect({ to: dest, replace: true });
  },
  component: RootLayout,
  notFoundComponent: NotFound,
});
